import react, { useEffect, useReducer } from 'react'
import api from '../../api'
import { lockedReducer } from '../../utils/defaultReducer'
import Loading from '../Loading'
import useNotification from '../Notification'

import {
    Alert,
    Box,
    Button,
    Divider,
    FormControlLabel,
    MenuItem,
    Paper,
    Switch,
    TextField,
    Typography,
} from '@mui/material'


const SIMPLE_NATIONAL_OPTIONS = [
    { value: '1', label: 'Não optante' },
    { value: '2', label: 'Optante - MEI' },
    { value: '3', label: 'Optante - ME/EPP do Simples Nacional' },
]

const ISS_TAX_TYPE_OPTIONS = [
    { value: '1', label: 'Operação tributável' },
    { value: '2', label: 'Imunidade' },
    { value: '3', label: 'Exportação de serviço' },
    { value: '4', label: 'Não incidência' },
]

const ISS_WITHHOLDING_TYPE_OPTIONS = [
    { value: '1', label: 'Não retido' },
    { value: '2', label: 'Retido pelo tomador' },
    { value: '3', label: 'Retido pelo intermediário' },
]

const SPECIAL_TAX_REGIME_OPTIONS = [
    { value: '0', label: '0 - Nenhum' },
]

const RTC_FIN_NFSE_OPTIONS = [
    { value: '0', label: '0 - Regular' },
    { value: '1', label: '1 - Crédito' },
    { value: '2', label: '2 - Débito' },
]

const normalizeIssRateForDisplay = (value) => {
    if (value === '' || value === null || value === undefined) return ''

    const numericValue = Number(value)
    if (Number.isNaN(numericValue)) return String(value)

    return numericValue.toFixed(2)
}

const parseGenericPercentageForPayload = (value) => {
    if (value === "" || value === null || value === undefined) return null

    const normalizedValue = Number(String(value).replace(',', '.'))
    if (Number.isNaN(normalizedValue)) return null

    if (normalizedValue < 0) return 0

    return Number(normalizedValue.toFixed(2))
}

const parseIssRateForPayload = (value) => {
    if (value === '' || value === null || value === undefined) return null

    const normalizedValue = Number(String(value).replace(',', '.'))
    if (Number.isNaN(normalizedValue)) return null

    if (normalizedValue < 0) return 0
    if (normalizedValue > 9.99) return 9.99

    return Number(normalizedValue.toFixed(2))
}

const shouldRequireIssRate = (state) => (
    state.simple_national_option === '3' && ['2', '3'].includes(state.iss_withholding_type)
)

const shouldWarnAboutPAliq = (state) => (
    state.simple_national_option === '3' && ['2', '3'].includes(state.iss_withholding_type)
)

const INITIAL_STATE = {
    loading: false,
    saving: false,
    usesDatabaseSettings: false,
    issuer_city_code: '',
    issuer_cpf_cnpj: '',
    issuer_municipal_registration: '',
    include_issuer_municipal_registration: false,
    series: '',
    national_tax_code: '',
    municipal_tax_code: '',
    iss_rate: '',
    federal_tax_rate: '',
    state_tax_rate: '',
    municipal_tax_rate: '',
    layout_version: '',
    application_version: '',
    simple_national_option: '',
    special_tax_regime: '',
    iss_tax_type: '',
    iss_withholding_type: '',
    rtc_enabled: false,
    rtc_fin_nfse: '0',
    rtc_nbs_code: '111032100',
    rtc_cst: '000',
    rtc_classification_code: '000001',
    rtc_operation_indicator_code: '',
    rtc_destination_indicator: '',
    rtc_personal_use_indicator: '',
}

const FORM_FIELDS = [
    'issuer_city_code',
    'issuer_cpf_cnpj',
    'issuer_municipal_registration',
    'include_issuer_municipal_registration',
    'series',
    'national_tax_code',
    'municipal_tax_code',
    'iss_rate',
    'layout_version',
    'application_version',
    'simple_national_option',
    'special_tax_regime',
    'iss_tax_type',
    'federal_tax_rate',
    'state_tax_rate',
    'municipal_tax_rate',
    'iss_withholding_type',
    'rtc_enabled',
    'rtc_fin_nfse',
    'rtc_nbs_code',
    'rtc_cst',
    'rtc_classification_code',
    'rtc_operation_indicator_code',
    'rtc_destination_indicator',
    'rtc_personal_use_indicator',
]

const loadSettings = async (setState) => {
    setState({ loading: true })
    const res = await api.get('/api/payment_national_nfse_settings/')

    if (res.status === 200) {
        const nextState = { loading: false }
        FORM_FIELDS.forEach((field) => {
            nextState[field] = res.data?.[field] ?? INITIAL_STATE[field]
        })
        nextState.usesDatabaseSettings = !!res.data?.uses_database_settings
        if (nextState.iss_rate !== '' && nextState.iss_rate !== null && nextState.iss_rate !== undefined) {
            nextState.iss_rate = normalizeIssRateForDisplay(nextState.iss_rate)
        }
        
        ['federal_tax_rate', 'state_tax_rate', 'municipal_tax_rate'].forEach((field) => {
            if (nextState[field] !== '' && nextState[field] !== null && nextState[field] !== undefined)
                nextState[field] = normalizeIssRateForDisplay(nextState[field])
        })
        setState(nextState)
        return
    }

    setState({ loading: false })
    useNotification('Ops!', 'Não foi possível carregar a configuração global da NFS-e.', 'danger')
}

const buildPayload = (state) => ({
    issuer_city_code: state.issuer_city_code,
    issuer_cpf_cnpj: state.issuer_cpf_cnpj,
    issuer_municipal_registration: state.issuer_municipal_registration,
    include_issuer_municipal_registration: !!state.include_issuer_municipal_registration,
    series: state.series,
    national_tax_code: state.national_tax_code,
    municipal_tax_code: state.municipal_tax_code,
    iss_rate: parseIssRateForPayload(state.iss_rate),
    federal_tax_rate: parseGenericPercentageForPayload(state.federal_tax_rate),
    state_tax_rate: parseGenericPercentageForPayload(state.state_tax_rate),
    municipal_tax_rate: parseGenericPercentageForPayload(state.municipal_tax_rate),
    layout_version: state.layout_version,
    application_version: state.application_version,
    simple_national_option: state.simple_national_option,
    special_tax_regime: state.special_tax_regime,
    iss_tax_type: state.iss_tax_type,
    iss_withholding_type: state.iss_withholding_type,
    rtc_enabled: !!state.rtc_enabled,
    rtc_fin_nfse: state.rtc_fin_nfse,
    rtc_nbs_code: state.rtc_nbs_code,
    rtc_cst: state.rtc_cst,
    rtc_classification_code: state.rtc_classification_code,
    rtc_operation_indicator_code: state.rtc_operation_indicator_code,
    rtc_destination_indicator: state.rtc_destination_indicator,
    rtc_personal_use_indicator: state.rtc_personal_use_indicator,
})

const saveSettings = async (state, setState) => {
    setState({ saving: true })
    const res = await api.patch('/api/payment_national_nfse_settings/', buildPayload(state))

    if (res.status === 200) {
        useNotification('Sucesso!', 'Configuração global da NFS-e salva com sucesso.', 'success')
        await loadSettings(setState)
        setState({ saving: false })
        return
    }

    setState({ saving: false })
    useNotification('Ops!', 'Não foi possível salvar a configuração global da NFS-e.', 'danger')
}

const inputPropsByField = {
    issuer_city_code: { label: 'Código do município do emitente', required: true },
    issuer_cpf_cnpj: { label: 'CPF/CNPJ do emitente', required: true },
    issuer_municipal_registration: { label: 'Inscrição municipal do emitente', required: true },
    series: { label: 'Série', required: true },
    national_tax_code: { label: 'Código nacional do serviço', required: true },
    municipal_tax_code: { label: 'Código de tributação municipal', required: true },
    iss_rate: {
        label: 'Alíquota do ISS (%)',
        type: 'number',
        inputProps: { step: '0.01', min: '0', max: '9.99' },
    },
    federal_tax_rate: {
        label: 'Percentual federal (%)',
        type: 'number',
        inputProps: { step: '0.01', min: '0' },
    },
    state_tax_rate: {
        label: 'Percentual estadual (%)',
        type: 'number',
        inputProps: { step: '0.01', min: '0' },
    },
    municipal_tax_rate: {
        label: 'Percentual municipal (%)',
        type: 'number',
        inputProps: { step: '0.01', min: '0' },
    },
    layout_version: { label: 'Versão do layout', required: true },
    application_version: { label: 'Versão da aplicação', required: true },
    simple_national_option: {
        label: 'Situação perante o Simples Nacional',
        required: true,
        select: true,
        options: SIMPLE_NATIONAL_OPTIONS,
    },
    special_tax_regime: {
        label: 'Regime especial de tributação',
        required: true,
        select: true,
        options: SPECIAL_TAX_REGIME_OPTIONS,
    },
    iss_tax_type: {
        label: 'Tributação do ISSQN',
        required: true,
        select: true,
        options: ISS_TAX_TYPE_OPTIONS,
    },
    iss_withholding_type: {
        label: 'Tipo de retenção do ISSQN',
        required: true,
        select: true,
        options: ISS_WITHHOLDING_TYPE_OPTIONS,
    },
    rtc_fin_nfse: {
        label: 'Finalidade da NFS-e',
        required: true,
        select: true,
        options: RTC_FIN_NFSE_OPTIONS,
    },
    rtc_nbs_code: {
        label: 'Código NBS',
    },
    rtc_cst: {
        label: 'Código CST',
    },
    rtc_classification_code: {
        label: 'Classificação tributária IBS/CBS',
    },
    rtc_operation_indicator_code: {
        label: 'Indicador da Operação',
    },
    rtc_destination_indicator: {
        label: 'Indicator de destino',
    },
    rtc_personal_use_indicator: {
        label: 'Indicador de uso pessoal',
        select: true,
        options: [
            { value: '0', label: '0 - Não' },
            { value: '1', label: '1 - Sim' },
        ],
    },
}

const fieldRows = [
    ['issuer_city_code', 'issuer_cpf_cnpj'],
    ['issuer_municipal_registration', 'series'],
    ['national_tax_code', 'municipal_tax_code'],
    ['iss_rate', 'federal_tax_rate'],
    ['state_tax_rate', 'municipal_tax_rate'],
    ['layout_version', 'application_version'],
    ['simple_national_option', 'special_tax_regime'],
    ['iss_tax_type', 'iss_withholding_type'],
]

const rtcFieldRows = [
    ['rtc_fin_nfse'],
    ['rtc_nbs_code', 'rtc_cst'],
    ['rtc_classification_code'],
    ['rtc_operation_indicator_code', 'rtc_destination_indicator'],
    ['rtc_personal_use_indicator'],
]

const NationalNFSeSettingsComponent = () => {
    const [state, setState] = useReducer(lockedReducer, INITIAL_STATE)

    useEffect(() => {
        loadSettings(setState)
    }, [])

    return (
        <Box
            className="plans-container"
            sx={{
                p: 2,
                height: '100%',
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
            }}
        >
            <Loading loading={state.loading || state.saving} />

            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', maxWidth: 1100 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h6">Configuração global da NFS-e nacional</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Esta tela controla apenas os parâmetros fiscais globais usados pelo fluxo nacional.
                        </Typography>                        
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" onClick={() => loadSettings(setState)}>
                            Recarregar
                        </Button>
                        <Button variant="contained" onClick={() => saveSettings(state, setState)}>
                            Salvar
                        </Button>
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Alert severity={state.usesDatabaseSettings ? 'success' : 'warning'} sx={{ mb: 2 }}>
                    {state.usesDatabaseSettings
                        ? 'A configuração atual está sendo lida do banco de dados.'
                        : 'Ainda não há configuração salva no banco. O serviço está usando fallback de variáveis de ambiente.'}
                </Alert>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {state.simple_national_option === '1' && (
                        <Alert severity="info">
                            Para não optante, a alíquota do ISS pode permanecer cadastrada para referência interna, mas não deve ser enviada como <b>pAliq</b> no XML.
                        </Alert>
                    )}

                    {shouldWarnAboutPAliq(state) && (
                        <Alert severity="info">
                            Esta combinação tributária exige o envio da alíquota do ISS no XML (<b>pAliq</b>). O valor será obtido do campo "Alíquota do ISS (%)".
                        </Alert>
                    )}

                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!state.include_issuer_municipal_registration}
                                onChange={(event) => setState({ include_issuer_municipal_registration: event.target.checked })}
                            />
                        }
                        label="Informar inscrição municipal do emitente"
                    />

                    {fieldRows.map((row, index) => (
                        <Box key={index} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: row.length > 1 ? '1fr 1fr' : '1fr' }, gap: 2 }}>
                            {row.map((field) => (
                                <TextField
                                    key={field}
                                    label={inputPropsByField[field].label}
                                    type={inputPropsByField[field].type || 'text'}
                                    size="small"
                                    required={field === 'iss_rate' ? shouldRequireIssRate(state) : !!inputPropsByField[field].required}
                                    value={state[field] ?? ''}
                                    onChange={(event) => setState({ [field]: event.target.value })}
                                    fullWidth
                                    disabled={field === 'iss_rate' && state.simple_national_option === '1'}
                                    inputProps={inputPropsByField[field].inputProps || undefined}
                                    select={!!inputPropsByField[field].select}
                                    helperText={field === 'iss_rate'
                                        ? 'Informe a alíquota em percentual com até 2 casas. Ex.: 3.00'
                                        : ['federal_tax_rate', 'state_tax_rate', 'municipal_tax_rate'].includes(field)
                                            ? 'Informe o percentual com até 2 casas. Ex.: 13.45'
                                            : undefined}
                                >
                                    {inputPropsByField[field].select && inputPropsByField[field].options.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            ))}
                        </Box>
                    ))}

                    <Divider sx={{ my: 1 }} />

                    <Typography variant="subtitle1">Reforma Tributária (IBS/CBS)</Typography>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!state.rtc_enabled}
                                onChange={(event) => setState({ rtc_enabled: event.target.checked })}
                            />
                        }
                        label="Habilitar grupo IBS/CBS"
                    />

                   {rtcFieldRows.map((row, index) => (
                        <Box key={`rtc-${index}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: row.length > 1 ? '1fr 1fr' : '1fr' }, gap: 2 }}>
                            {row.map((field) => (
                                <TextField
                                    key={field}
                                    label={inputPropsByField[field].label}
                                    type={inputPropsByField[field].type || 'text'}
                                    size="small"
                                    required={!!state.rtc_enabled}
                                    value={state[field] ?? ''}
                                    onChange={(event) => setState({ [field]: event.target.value })}
                                    fullWidth
                                    disabled={!state.rtc_enabled}
                                    inputProps={inputPropsByField[field].inputProps || undefined}
                                    select={!!inputPropsByField[field].select}
                                    // helperText={
                                    //     field === 'rtc_nbs_code'
                                    //         ? 'Example from current reference invoice: 111032100'
                                    //         : field === 'rtc_cst'
                                    //             ? 'Example from current reference invoice: 000'
                                    //             : field === 'rtc_classification_code'
                                    //                 ? 'Example from current reference invoice: 000001'
                                    //                 : field === 'rtc_fin_nfse'
                                    //                     ? 'Use 0 for regular issuance.'
                                    //                     : field === 'rtc_operation_indicator_code'
                                    //                         ? 'Required before enabling the IBS/CBS XML group.'
                                    //                         : field === 'rtc_destination_indicator'
                                    //                             ? 'Required before enabling the IBS/CBS XML group.'
                                    //                             : field === 'rtc_personal_use_indicator'
                                    //                                 ? 'Required before enabling the IBS/CBS XML group.'
                                    //                                 : undefined                                    
                                    // }
                                >
                                    {inputPropsByField[field].select && inputPropsByField[field].options.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Paper>
        </Box>
    )
}

export default NationalNFSeSettingsComponent
